/**
 * A QR symbol, written by hand, for the one thing this product needs one for.
 *
 * Enrolling an authenticator used to show only the base32 secret —
 * `4IEQ 45FH 3T5O LQOJ 6SYS XAAO VLO7 MO7R`, thirty-two characters, typed into a phone. It is easy
 * to get one character wrong and there is no feedback when you do: the app happily produces codes,
 * and every one of them is refused, with nothing on either screen to say why. The camera is the
 * right answer, and the text stays beside it for the desktop authenticator, the broken camera and
 * the password manager that takes a typed key.
 *
 * ## Why this is written here rather than installed
 *
 * The honest argument against writing it is that a QR encoder is not a bar chart. It is Reed–Solomon
 * over GF(256), version and mode selection, eight data masks scored against each other, and two
 * BCH-coded bit fields that tell the scanner how to read everything else. Getting any of that subtly
 * wrong yields a symbol that some readers accept and others silently refuse, which is the worst
 * failure available to a thing whose entire job is to be scanned. That is a real argument and it is
 * why ADR-0092's and ADR-0107's precedent is not simply inherited.
 *
 * What settles it is that the risk is **testable to exhaustion, offline, in a second**:
 *
 * - `tools/verify-qr.mjs` compares every symbol this file produces, module for module, against
 *   `qrcode-generator` — Arase's reference implementation, twenty years deployed — at each of the
 *   eight masks, and asserts that exactly one mask makes the two matrices identical. Exactly one
 *   can only happen if the codewords, the interleaving, the function patterns, the module placement,
 *   the mask application *and* the format bits all agree.
 * - It then rasterises what the browser actually paints and decodes it with `jsQR`, which is
 *   neither this file nor the oracle, and asserts the decoded string equals the `otpauth://` URI
 *   character for character — at the size the screens really render, and smaller.
 *
 * Both of those are **devDependencies**. They run on our machines and never reach a customer's
 * browser. A runtime dependency here would be a different bargain: `@rentorng/core` is imported by
 * the API, the agency app, the listing site, the console *and* the Expo bundle, so one package
 * added here is five bundles enlarged and one more standing obligation for as long as the product
 * exists. A test-only oracle costs nothing at runtime and gives a stronger guarantee than shipping
 * the library would, because it is checked on every run rather than trusted.
 *
 * ## What is supported, and what is refused
 *
 * **Byte mode, error correction level M, versions 1 to 20.** That is the whole of it, deliberately.
 * An `otpauth://` URI is percent-encoded ASCII with lowercase letters and `?`, `&`, `=`, `:` and
 * `@` in it, so the alphanumeric and numeric modes cannot represent it and there is nothing to
 * choose between. Level M — fifteen per cent recoverable — is what every authenticator's own
 * enrolment code uses; the level is in the format bits, so a reader is told which one it is
 * looking at and no reader cares which we picked. Version 20 holds 666 bytes in byte mode, against
 * the longest URI this product can mint at about 230.
 *
 * Anything that does not fit **throws** rather than being truncated into a symbol that scans and
 * yields half a secret. That is the same choice ADR-0092 makes about archives above 4GB: one that
 * opens in some readers and not others is worse than one that was never made.
 *
 * ## Why the markup is a string
 *
 * `@rentorng/core` holds pure functions and no React — and the console deliberately shares no
 * component with the agency application (see the header of `apps/admin/src/ui.tsx`; a shared
 * button is how a shared session helper follows). The loader already solves exactly this: the
 * geometry lives here as `loaderMarkup()`, and each surface wraps it in a component of its own.
 * The same shape is used here. `qrSvg()` is the single implementation of everything that can be
 * got wrong; `QrCode` in `apps/admin/src/ui.tsx` and in `apps/web/src/components/qr-code.tsx` are
 * a dozen lines each of layout, and neither can drift into a different encoder.
 *
 * ## Why the colours are not themed
 *
 * The symbol is black on white and carries its own four-module quiet zone, on the console's slate
 * ground as much as on the agency app's white one. A scanner looks for a light border and a
 * high-contrast pattern; tinting either to match a surface, or letting a dark card crop the margin,
 * is how a code becomes one that reads on the developer's phone and not on the customer's. The
 * quiet zone is inside the SVG rather than left to a CSS padding for the same reason — padding is
 * one careless `p-0` away from being gone.
 */

/* ------------------------------------------------------------------ the tables */

/**
 * Block structure at level M, indexed by version.
 *
 * `[error-correction codewords per block, blocks in group 1, data codewords per group 1 block,
 * blocks in group 2]`. A group 2 block always holds exactly one data codeword more than a group 1
 * block, which is why that number is not stored. ISO/IEC 18004 table 9.
 */
const BLOCKS: ReadonlyArray<readonly [number, number, number, number]> = [
  [0, 0, 0, 0], // no version 0
  [10, 1, 16, 0],
  [16, 1, 28, 0],
  [26, 1, 44, 0],
  [18, 2, 32, 0],
  [24, 2, 43, 0],
  [16, 4, 27, 0],
  [18, 4, 31, 0],
  [22, 2, 38, 2],
  [22, 3, 36, 2],
  [26, 4, 43, 1],
  [30, 1, 50, 4],
  [22, 6, 36, 2],
  [22, 8, 37, 1],
  [24, 4, 40, 5],
  [24, 5, 41, 5],
  [28, 7, 45, 3],
  [28, 10, 46, 1],
  [26, 9, 43, 4],
  [26, 3, 44, 11],
  [26, 3, 41, 13],
];

/** The highest version this file will produce. See the header for why it stops here. */
const MAX_VERSION = BLOCKS.length - 1;

/** Row and column centres of the alignment patterns, indexed by version. ISO/IEC 18004 annex E. */
const ALIGNMENT: ReadonlyArray<readonly number[]> = [
  [],
  [], // version 1 has none
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
  [6, 30, 54],
  [6, 32, 58],
  [6, 34, 62],
  [6, 26, 46, 66],
  [6, 26, 48, 70],
  [6, 26, 50, 74],
  [6, 30, 54, 78],
  [6, 30, 56, 82],
  [6, 30, 58, 86],
  [6, 34, 62, 90],
];

/** How many data codewords a version holds at level M, once the blocks are added up. */
function dataCodewords(version: number): number {
  const [, group1, perBlock, group2] = BLOCKS[version];
  return group1 * perBlock + group2 * (perBlock + 1);
}

/* ------------------------------------------------------------------- GF(256) */

/*
  The field the error correction is computed in: bytes, with multiplication modulo the primitive
  polynomial x⁸ + x⁴ + x³ + x² + 1 (0x11d). Logs and antilogs are built once so a multiplication is
  two lookups and an addition rather than a loop over bits. The antilog table is doubled so the
  exponent sum never has to be reduced.
*/
const EXP = new Uint8Array(512);
const LOG = new Uint8Array(256);
{
  let value = 1;
  for (let i = 0; i < 255; i += 1) {
    EXP[i] = value;
    LOG[value] = i;
    value <<= 1;
    if (value & 0x100) value ^= 0x11d;
  }
  for (let i = 255; i < 512; i += 1) EXP[i] = EXP[i - 255];
}

const multiply = (a: number, b: number): number => (a === 0 || b === 0 ? 0 : EXP[LOG[a] + LOG[b]]);

/**
 * The generator polynomial for `count` error-correction codewords: ∏(x − α^i) for i in 0…count−1.
 * Coefficients are highest degree first, so `poly[0]` is always 1.
 */
function generatorPolynomial(count: number): number[] {
  let poly = [1];
  for (let i = 0; i < count; i += 1) {
    const next = new Array<number>(poly.length + 1).fill(0);
    for (let j = 0; j < poly.length; j += 1) {
      next[j] ^= poly[j];
      next[j + 1] ^= multiply(poly[j], EXP[i]);
    }
    poly = next;
  }
  return poly;
}

/** The remainder of the data polynomial divided by the generator — the error-correction codewords. */
function errorCorrection(data: readonly number[], count: number): number[] {
  const generator = generatorPolynomial(count);
  const remainder = new Array<number>(count).fill(0);
  for (const byte of data) {
    const factor = byte ^ remainder[0];
    remainder.shift();
    remainder.push(0);
    for (let i = 0; i < count; i += 1) remainder[i] ^= multiply(generator[i + 1], factor);
  }
  return remainder;
}

/* --------------------------------------------------------------- the codewords */

/** Bits, most significant first, accumulated into whole bytes. */
class BitBuffer {
  readonly bytes: number[] = [];
  length = 0;

  put(value: number, count: number) {
    for (let i = count - 1; i >= 0; i -= 1) {
      const index = this.length >>> 3;
      if (this.bytes.length <= index) this.bytes.push(0);
      if ((value >>> i) & 1) this.bytes[index] |= 0x80 >>> (this.length & 7);
      this.length += 1;
    }
  }
}

/**
 * The smallest version that will hold these bytes at level M.
 *
 * The character-count field is eight bits up to version 9 and sixteen from version 10, so the
 * threshold is not simply "more bytes, bigger symbol" — crossing into version 10 costs a byte of
 * header as well. Working it out per version rather than from a capacity table keeps the two from
 * disagreeing.
 */
function chooseVersion(byteCount: number): number {
  for (let version = 1; version <= MAX_VERSION; version += 1) {
    const countBits = version <= 9 ? 8 : 16;
    if (4 + countBits + byteCount * 8 <= dataCodewords(version) * 8) return version;
  }
  throw new Error(
    `A QR symbol for ${byteCount} bytes would need more than version ${MAX_VERSION}. Refusing rather than truncating it.`,
  );
}

/** Header, payload, terminator, padding — the data codewords, before they are split into blocks. */
function codewords(bytes: Uint8Array, version: number): number[] {
  const capacity = dataCodewords(version);
  const buffer = new BitBuffer();
  buffer.put(0b0100, 4); // byte mode
  buffer.put(bytes.length, version <= 9 ? 8 : 16);
  for (const byte of bytes) buffer.put(byte, 8);

  // Up to four zero bits to say the message has ended, then zeros to the next byte boundary.
  const terminator = Math.min(4, capacity * 8 - buffer.length);
  buffer.put(0, terminator);
  if (buffer.length & 7) buffer.put(0, 8 - (buffer.length & 7));

  const data = buffer.bytes.slice();
  // The two pad codewords the standard names, alternating, to fill the version out.
  for (let i = 0; data.length < capacity; i += 1) data.push(i % 2 === 0 ? 0xec : 0x11);
  return data;
}

/**
 * Split into blocks, correct each, then interleave.
 *
 * The interleaving is the point of the exercise: a scratch across the symbol damages one codeword
 * of many blocks rather than many codewords of one, and each block can then be repaired within its
 * own budget. Data codewords come first, taken a column at a time across the blocks; the
 * error-correction codewords follow in the same manner.
 */
function interleave(data: readonly number[], version: number): number[] {
  const [ecPerBlock, group1, perBlock, group2] = BLOCKS[version];

  const blocks: number[][] = [];
  const parity: number[][] = [];
  let cursor = 0;
  for (let i = 0; i < group1 + group2; i += 1) {
    const size = i < group1 ? perBlock : perBlock + 1;
    const block = data.slice(cursor, cursor + size);
    cursor += size;
    blocks.push(block);
    parity.push(errorCorrection(block, ecPerBlock));
  }

  const out: number[] = [];
  for (let i = 0; i < perBlock + 1; i += 1) {
    for (const block of blocks) if (i < block.length) out.push(block[i]);
  }
  for (let i = 0; i < ecPerBlock; i += 1) {
    for (const block of parity) out.push(block[i]);
  }
  return out;
}

/* ------------------------------------------------------------------ the symbol */

/**
 * The grid, in `[row][column]`, with a parallel record of which modules belong to the function
 * patterns. Function modules are never masked and never carry data, and keeping that as its own
 * matrix rather than inferring it from coordinates is what stops the placement walk from writing
 * over a timing pattern.
 */
interface Grid {
  size: number;
  dark: boolean[][];
  reserved: boolean[][];
}

function blank(version: number): Grid {
  const size = version * 4 + 17;
  return {
    size,
    dark: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
    reserved: Array.from({ length: size }, () => new Array<boolean>(size).fill(false)),
  };
}

/** Set a function module: dark or light, and off limits to the data walk either way. */
function fixed(grid: Grid, x: number, y: number, dark: boolean) {
  if (x < 0 || y < 0 || x >= grid.size || y >= grid.size) return;
  grid.dark[y][x] = dark;
  grid.reserved[y][x] = true;
}

const bitOf = (value: number, index: number): boolean => ((value >>> index) & 1) === 1;

/** The three big squares that let a scanner find the symbol at all, with their light separators. */
function drawFinder(grid: Grid, x: number, y: number) {
  for (let dy = -4; dy <= 4; dy += 1) {
    for (let dx = -4; dx <= 4; dx += 1) {
      const reach = Math.max(Math.abs(dx), Math.abs(dy));
      fixed(grid, x + dx, y + dy, reach !== 2 && reach !== 4);
    }
  }
}

/** The alternating line along row and column 6 that tells a scanner how wide a module is. */
function drawTiming(grid: Grid) {
  for (let i = 0; i < grid.size; i += 1) {
    fixed(grid, 6, i, i % 2 === 0);
    fixed(grid, i, 6, i % 2 === 0);
  }
}

/** The smaller squares that let a scanner correct for a symbol photographed at an angle. */
function drawAlignment(grid: Grid, version: number) {
  const positions = ALIGNMENT[version];
  for (let i = 0; i < positions.length; i += 1) {
    for (let j = 0; j < positions.length; j += 1) {
      // The three corners already hold a finder pattern; an alignment pattern is not drawn there.
      const corner =
        (i === 0 && j === 0) ||
        (i === 0 && j === positions.length - 1) ||
        (i === positions.length - 1 && j === 0);
      if (corner) continue;
      for (let dy = -2; dy <= 2; dy += 1) {
        for (let dx = -2; dx <= 2; dx += 1) {
          fixed(grid, positions[i] + dx, positions[j] + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1);
        }
      }
    }
  }
}

/**
 * The error-correction level and the mask, twice, in a BCH(15,5) code.
 *
 * Written twice because it is the one field a scanner cannot recover without: if the copy beside
 * the top-left finder is damaged, the copy split between the other two is read instead. The final
 * XOR with 0x5412 exists so that an all-zero field — level M, mask 0 — is not an all-light run
 * that a scanner would mistake for a quiet zone.
 */
function drawFormat(grid: Grid, mask: number) {
  const value = (0b00 << 3) | mask; // 0b00 is level M
  let remainder = value;
  for (let i = 0; i < 10; i += 1) remainder = (remainder << 1) ^ ((remainder >>> 9) * 0x537);
  const bits = ((value << 10) | remainder) ^ 0x5412;

  // The first copy runs down column 8 and then left along row 8, stepping over the two timing
  // modules at (6, 8) and (8, 6) — which is the whole reason this is written out rather than looped.
  for (let i = 0; i <= 5; i += 1) fixed(grid, 8, i, bitOf(bits, i));
  fixed(grid, 8, 7, bitOf(bits, 6));
  fixed(grid, 8, 8, bitOf(bits, 7));
  fixed(grid, 7, 8, bitOf(bits, 8));
  for (let i = 9; i < 15; i += 1) fixed(grid, 14 - i, 8, bitOf(bits, i));

  for (let i = 0; i < 8; i += 1) fixed(grid, grid.size - 1 - i, 8, bitOf(bits, i));
  for (let i = 8; i < 15; i += 1) fixed(grid, 8, grid.size - 15 + i, bitOf(bits, i));

  // The module the standard requires to be dark, always, in every symbol.
  fixed(grid, 8, grid.size - 8, true);
}

/** The version number, in a BCH(18,6) code, twice — only from version 7, where it is needed. */
function drawVersion(grid: Grid, version: number) {
  if (version < 7) return;
  let remainder = version;
  for (let i = 0; i < 12; i += 1) remainder = (remainder << 1) ^ ((remainder >>> 11) * 0x1f25);
  const bits = (version << 12) | remainder;
  for (let i = 0; i < 18; i += 1) {
    const bit = bitOf(bits, i);
    const far = grid.size - 11 + (i % 3);
    const near = Math.floor(i / 3);
    fixed(grid, far, near, bit);
    fixed(grid, near, far, bit);
  }
}

/**
 * The data, walked up and down two columns at a time from the bottom right corner.
 *
 * Column 6 is skipped entirely because the vertical timing pattern occupies it. Any module the walk
 * reaches after the codewords have run out is left light: those are the standard's remainder bits,
 * and they are masked along with everything else, which is why they are written here rather than
 * left out of the grid.
 */
function drawData(grid: Grid, stream: readonly number[]) {
  let bit = 0;
  for (let right = grid.size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    for (let step = 0; step < grid.size; step += 1) {
      for (let column = 0; column < 2; column += 1) {
        const x = right - column;
        const upward = ((right + 1) & 2) === 0;
        const y = upward ? grid.size - 1 - step : step;
        if (grid.reserved[y][x]) continue;
        if (bit < stream.length * 8) {
          grid.dark[y][x] = bitOf(stream[bit >>> 3], 7 - (bit & 7));
          bit += 1;
        }
      }
    }
  }
}

/** The eight masks, by number. `x` is the column and `y` the row, as the standard has them. */
function masked(mask: number, x: number, y: number): boolean {
  switch (mask) {
    case 0:
      return (x + y) % 2 === 0;
    case 1:
      return y % 2 === 0;
    case 2:
      return x % 3 === 0;
    case 3:
      return (x + y) % 3 === 0;
    case 4:
      return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0;
    case 5:
      return ((x * y) % 2) + ((x * y) % 3) === 0;
    case 6:
      return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0;
    default:
      return ((((x + y) % 2) + ((x * y) % 3)) % 2) === 0;
  }
}

function applyMask(grid: Grid, mask: number) {
  for (let y = 0; y < grid.size; y += 1) {
    for (let x = 0; x < grid.size; x += 1) {
      if (!grid.reserved[y][x] && masked(mask, x, y)) grid.dark[y][x] = !grid.dark[y][x];
    }
  }
}

/**
 * How badly a masked symbol reads, by the standard's four rules — lower is better.
 *
 * The rules are about a scanner's eyes, not about correctness: long runs of one colour are hard to
 * count, large blocks confuse the binariser, anything resembling a finder pattern sends it looking
 * for a corner that is not there, and a symbol far off half dark loses contrast under a poor
 * exposure. **Every mask produces a valid symbol** — which one was used is in the format bits — so
 * this only ever chooses between eight correct answers.
 */
function penalty(grid: Grid): number {
  const { size, dark } = grid;
  let score = 0;

  // Rule 1 — five or more of one colour in a row, in either direction.
  for (let pass = 0; pass < 2; pass += 1) {
    for (let a = 0; a < size; a += 1) {
      let run = 1;
      for (let b = 1; b < size; b += 1) {
        const here = pass === 0 ? dark[a][b] : dark[b][a];
        const before = pass === 0 ? dark[a][b - 1] : dark[b - 1][a];
        if (here === before) {
          run += 1;
          if (run === 5) score += 3;
          else if (run > 5) score += 1;
        } else {
          run = 1;
        }
      }
    }
  }

  // Rule 2 — every 2×2 block of a single colour.
  for (let y = 0; y < size - 1; y += 1) {
    for (let x = 0; x < size - 1; x += 1) {
      const first = dark[y][x];
      if (first === dark[y][x + 1] && first === dark[y + 1][x] && first === dark[y + 1][x + 1]) score += 3;
    }
  }

  // Rule 3 — the 1:1:3:1:1 run of a finder pattern with four light modules beside it, either way
  // round, in rows and in columns. The four light modules may run off the edge of the symbol, where
  // the quiet zone supplies them, so the window is read past the boundary as light.
  const at = (a: number, b: number, vertical: boolean): boolean => {
    if (b < 0 || b >= size) return false;
    return vertical ? dark[b][a] : dark[a][b];
  };
  const FINDER = [true, false, true, true, true, false, true];
  for (let vertical = 0; vertical < 2; vertical += 1) {
    for (let a = 0; a < size; a += 1) {
      for (let b = 0; b <= size - 7; b += 1) {
        let core = true;
        for (let i = 0; i < 7 && core; i += 1) core = at(a, b + i, vertical === 1) === FINDER[i];
        if (!core) continue;
        const before = [1, 2, 3, 4].every((gap) => !at(a, b - gap, vertical === 1));
        const after = [0, 1, 2, 3].every((gap) => !at(a, b + 7 + gap, vertical === 1));
        if (before || after) score += 40;
      }
    }
  }

  // Rule 4 — how far the proportion of dark modules is from half.
  let count = 0;
  for (let y = 0; y < size; y += 1) for (let x = 0; x < size; x += 1) if (dark[y][x]) count += 1;
  const percent = (count * 100) / (size * size);
  score += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return score;
}

/* -------------------------------------------------------------------- the API */

export interface QrOptions {
  /**
   * Force a mask instead of scoring all eight.
   *
   * Only `tools/verify-qr.mjs` passes this, and only so that this file and the reference
   * implementation it is checked against can be compared at the same mask — the two score the
   * candidates by different heuristics, both legitimate, so they need not choose the same one.
   * Nothing in the applications sets it.
   */
  mask?: number;
}

/**
 * The symbol as a grid of booleans, `[row][column]`, dark where true. No quiet zone: that belongs
 * to whatever draws it, and `qrSvg()` puts it in.
 *
 * Throws if the text will not fit in version 20 at level M — see the header on why this refuses
 * rather than truncates.
 */
export function qrMatrix(text: string, options: QrOptions = {}): boolean[][] {
  const bytes = new TextEncoder().encode(text);
  const version = chooseVersion(bytes.length);
  const stream = interleave(codewords(bytes, version), version);

  const build = (mask: number): Grid => {
    const grid = blank(version);
    drawTiming(grid);
    drawFinder(grid, 3, 3);
    drawFinder(grid, grid.size - 4, 3);
    drawFinder(grid, 3, grid.size - 4);
    drawAlignment(grid, version);
    drawFormat(grid, mask);
    drawVersion(grid, version);
    drawData(grid, stream);
    applyMask(grid, mask);
    return grid;
  };

  if (options.mask !== undefined) return build(options.mask).dark;

  let best: Grid | null = null;
  let bestScore = Infinity;
  for (let mask = 0; mask < 8; mask += 1) {
    const candidate = build(mask);
    const score = penalty(candidate);
    if (score < bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return best!.dark;
}

/** Modules of light around the symbol. Four is the standard's minimum and scanners rely on it. */
export const QR_QUIET_ZONE = 4;

const escapeAttribute = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * The symbol as SVG markup, ready for `dangerouslySetInnerHTML`.
 *
 * One `<path>` of horizontal runs rather than a `<rect>` per module: a version 10 symbol has
 * something like sixteen hundred dark modules and that many elements is a slow, enormous piece of
 * DOM for a picture that never changes.
 *
 * `shape-rendering="crispEdges"` matters more than it looks. Anti-aliasing a module boundary at
 * three device pixels per module puts a grey halo on every edge, and a binariser reading a
 * photograph of that screen has to guess. Crisp edges make every module the same solid colour, at
 * the cost of one module occasionally being a pixel wider than its neighbour, which no scanner
 * minds. `width` and `height` are left to the caller's box so the two applications can size it to
 * their own layout without either being able to reach inside and recolour it.
 *
 * @param text  What the scanner should read. Reproduced exactly, byte for byte.
 * @param label The accessible name. Say what scanning it will do, not that it is a QR code.
 */
export function qrSvg(text: string, label: string): string {
  const modules = qrMatrix(text);
  const size = modules.length;
  const extent = size + QR_QUIET_ZONE * 2;

  const runs: string[] = [];
  for (let y = 0; y < size; y += 1) {
    let x = 0;
    while (x < size) {
      if (!modules[y][x]) {
        x += 1;
        continue;
      }
      let run = 1;
      while (x + run < size && modules[y][x + run]) run += 1;
      runs.push(`M${x + QR_QUIET_ZONE} ${y + QR_QUIET_ZONE}h${run}v1h-${run}z`);
      x += run;
    }
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${extent} ${extent}" width="100%" height="100%"` +
    ` shape-rendering="crispEdges" role="img" aria-label="${escapeAttribute(label)}" focusable="false">` +
    // The white ground is part of the picture, not the page's: it is what carries the quiet zone
    // onto a dark surface, where the card behind it would otherwise crop the margin away.
    `<rect width="${extent}" height="${extent}" fill="#ffffff"/>` +
    `<path fill="#000000" d="${runs.join("")}"/>` +
    `</svg>`
  );
}
